import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Flame, Info } from "lucide-react";
import { journeyApi } from "@/lib/journeyApi";
import { fmt } from "@/lib/format";
import { track } from "@/lib/analytics";

// Educational Financial Independence (Freedom) scenario. Every assumption is the
// customer's own; The Paycheck Edit recommends no investments and guarantees nothing.
export function FreedomScenario() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [monthly, setMonthly] = useState("");
  const [growth, setGrowth] = useState("");
  const [calcing, setCalcing] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [assetForm, setAssetForm] = useState(false);
  const [assetVal, setAssetVal] = useState("");
  const [savingAssets, setSavingAssets] = useState(false);
  const navigate = useNavigate();

  const saveAssets = async () => {
    const v = Math.round(Number(assetVal) * 100) / 100;
    if (!Number.isFinite(v) || v < 0) { toast.error("Enter your current assets."); return; }
    setSavingAssets(true);
    try {
      await journeyApi.updateFiAssets(v);
      const d = await journeyApi.freedomScenario();
      setS(d);
      setAssetForm(false);
      toast.success("Current assets updated.");
    } catch { toast.error("Could not update current assets."); }
    finally { setSavingAssets(false); }
  };

  const saveScenario = async () => {
    setSaving(true);
    try {
      const d = await journeyApi.saveFreedomScenario({ monthly_contribution: monthly ? parseFloat(monthly) : 0, growth_rate: growth ? parseFloat(growth) : 0 });
      setS((prev) => ({ ...prev, ...d }));
      setSaved(true);
      toast.success("Scenario saved.");
    } catch { toast.error("Could not save your scenario."); }
    finally { setSaving(false); }
  };

  useEffect(() => {
    let cancelled = false;
    journeyApi.freedomScenario()
      .then((d) => {
        if (cancelled) return;
        setS(d);
        if (d?.has_scenario) track("freedom_scenario_viewed");
        if (d?.monthly_contribution) setMonthly(String(d.monthly_contribution));
        if (d?.growth_rate) setGrowth(String(d.growth_rate));
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!s?.has_scenario) return;
    const m = parseFloat(monthly);
    if (!Number.isFinite(m) || m < 0) return;
    let cancelled = false;
    setCalcing(true);
    setSaved(false);
    const t = setTimeout(() => {
      journeyApi.freedomScenario(m || 0, growth ? parseFloat(growth) : 0)
        .then((d) => { if (!cancelled) setS((prev) => ({ ...prev, projection: d.projection, monthly_contribution: d.monthly_contribution, growth_rate: d.growth_rate })); })
        .catch(() => {})
        .finally(() => { if (!cancelled) setCalcing(false); });
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthly, growth]);

  if (loading) return <div className="mt-4 h-28 animate-pulse rounded-2xl bg-[#F0EBE2]" data-testid="freedom-scenario-loading" />;
  if (!s?.has_goal) return null;
  if (!s?.has_scenario) {
    return (
      <div className="mt-4 rounded-2xl border border-[#C9A86A]/50 bg-[#FBF7EF] p-5" data-testid="freedom-scenario-validation">
        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[#9A7B3A]"><Flame className="h-4 w-4" /> Freedom scenario</p>
        <p className="mt-2 text-sm leading-relaxed text-[#70584A]" data-testid="freedom-rate-validation">
          {s.validation || "Enter a withdrawal rate greater than 0% to calculate an illustrative Financial Independence target."}
        </p>
      </div>
    );
  }

  const proj = s.projection || {};
  return (
    <div className="mt-4 rounded-2xl border border-[#C9A86A]/50 bg-[#FBF7EF] p-5" data-testid="freedom-scenario">
      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[#9A7B3A]"><Flame className="h-4 w-4" /> Freedom scenario</p>
      <p className="mt-2 text-sm text-[#70584A]">
        To cover {fmt(s.annual_spending)}/year at a {s.withdrawal_rate || 4}% withdrawal rate (an illustrative assumption you choose), your Freedom number is{" "}
        <span className="font-semibold text-[#164C3B]" data-testid="freedom-number">{fmt(s.fi_number)}</span>.
      </p>
      <p className="mt-1 text-xs leading-relaxed text-[#8A7C6B]" data-testid="freedom-planning-note">
        Your Freedom number is a planning scenario, not a determination of when you can safely retire.
      </p>

      <div className="mt-4 grid grid-cols-1 gap-2 text-sm sm:grid-cols-3">
        <div className="min-w-0"><p className="text-[#A89A88]">Current progress</p><p className="font-medium text-[#164C3B] break-words" data-testid="freedom-progress">{fmt(s.progress)}</p></div>
        <div className="min-w-0"><p className="text-[#A89A88]">Gap</p><p className="font-medium text-[#171717] break-words" data-testid="freedom-gap">{fmt(s.gap)}</p></div>
        <div className="min-w-0"><p className="text-[#A89A88]">Funded</p><p className="font-medium text-[#171717] break-words">{s.percent != null ? `${s.percent}%` : "—"}</p></div>
      </div>
      <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-[#EADFC7]"><div className="h-full rounded-full bg-[#C9A86A] transition-[width] duration-[900ms] ease-out" style={{ width: `${s.percent || 0}%` }} data-testid="freedom-bar" /></div>

      {/* Current assets (user-reported) and confirmed contributions are shown
          SEPARATELY and are never summed into a single progress number. */}
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="rounded-xl border border-[#E5E0D9] bg-white p-3" data-testid="freedom-current-assets">
          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#A89A88]">Current assets</p>
          <p className="mt-0.5 font-serif text-xl text-[#164C3B] tabular-nums">{s.current_assets_reported ? fmt(s.current_assets) : "Not yet updated"}</p>
          <p className="mt-0.5 text-[11px] text-[#8A7C6B]">{s.current_assets_reported ? `User-reported${s.current_assets_updated_at ? ` · Updated ${new Date(s.current_assets_updated_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}` : ""}` : "Add your current balance to track progress"}</p>
          {!assetForm ? (
            <button type="button" data-testid="fi-update-assets-open" onClick={() => { setAssetVal(s.current_assets_reported ? String(s.current_assets) : ""); setAssetForm(true); }} className="mt-1.5 text-xs font-medium text-[#2F6B57] hover:underline">Update current assets</button>
          ) : (
            <div className="mt-2">
              <input inputMode="decimal" value={assetVal} data-testid="fi-assets-input" onChange={(e) => setAssetVal(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="$0"
                className="w-full rounded-lg border border-[#DDE7DF] px-3 py-2 text-sm text-[#171717] outline-none focus:border-[#2F6B57]" />
              <div className="mt-2 flex gap-2">
                <button type="button" data-testid="fi-assets-save" disabled={savingAssets} onClick={saveAssets} className="min-h-9 rounded-lg bg-[#2F6B57] px-3 text-xs font-semibold text-white hover:bg-[#255643] disabled:opacity-60">Save</button>
                <button type="button" data-testid="fi-assets-cancel" disabled={savingAssets} onClick={() => setAssetForm(false)} className="min-h-9 rounded-lg px-3 text-xs font-medium text-[#70584A] hover:bg-[#F5F2EC]">Cancel</button>
              </div>
            </div>
          )}
        </div>
        <div className="rounded-xl border border-[#E5E0D9] bg-white p-3" data-testid="freedom-confirmed-edits">
          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#A89A88]">Confirmed through Edits</p>
          <p className="mt-0.5 font-serif text-xl text-[#164C3B] tabular-nums">{fmt(s.confirmed_through_edits ?? s.contributed)}</p>
          <p className="mt-0.5 text-[11px] text-[#8A7C6B]">Historical confirmed contributions. Shown separately, not added to current assets.</p>
        </div>
      </div>

      {s.reached ? (
        <div data-testid="freedom-reached">
          <p className="mt-3 font-serif text-xl text-[#164C3B]">You've reached your Freedom number. 🔥</p>
          <button type="button" onClick={() => navigate("/dashboard")} data-testid="freedom-back-to-goal" className="mt-3 min-h-11 w-full rounded-xl border border-[#E5E0D9] text-sm font-semibold text-[#164C3B] hover:bg-[#F5F2EC] sm:w-auto sm:px-6">BACK TO MY GOAL</button>
        </div>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); saveScenario(); }} className="mt-4 rounded-xl pe-surface p-4">
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#70584A]">Project your timeline</p>
          <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs text-[#A89A88]">Add per month</label>
              <div className="relative mt-1">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#A89A88]">$</span>
                <input inputMode="decimal" value={monthly} onChange={(e) => setMonthly(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="e.g. 1500" data-testid="freedom-monthly-input"
                  className="min-h-11 w-full rounded-xl pe-surface pl-7 pr-3 text-[#171717] outline-none focus:border-[#2F6B57]" />
              </div>
            </div>
            <div>
              <label className="text-xs text-[#A89A88]">Growth assumption (optional)</label>
              <div className="relative mt-1">
                <input inputMode="decimal" value={growth} onChange={(e) => setGrowth(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="e.g. 5" data-testid="freedom-growth-input"
                  className="min-h-11 w-full rounded-xl pe-surface pl-3 pr-7 text-[#171717] outline-none focus:border-[#2F6B57]" />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#A89A88]">%</span>
              </div>
            </div>
          </div>
          {calcing ? (
            <p className="mt-3 text-xs text-[#A89A88]">Calculating…</p>
          ) : proj.reached && proj.label ? (
            <p className="mt-3 rounded-xl bg-[#DDE7DF] p-3 text-sm text-[#164C3B]" data-testid="freedom-projection">
              At this pace you'd reach your Freedom number in about <span className="font-semibold" data-testid="freedom-eta">{proj.label}</span>.
            </p>
          ) : (monthly && !proj.reached) ? (
            <p className="mt-3 text-sm text-[#8A7C6B]" data-testid="freedom-projection-none">Add a monthly amount to see a projected timeline.</p>
          ) : null}
          {saved && <p className="mt-3 rounded-xl bg-[#DDE7DF] p-2.5 text-center text-sm font-medium text-[#164C3B]" data-testid="freedom-scenario-saved">Scenario saved.</p>}
          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <button type="submit" disabled={saving} data-testid="freedom-save-scenario" className="min-h-11 flex-1 rounded-xl bg-[#2F6B57] text-sm font-semibold text-white hover:bg-[#255643] disabled:opacity-60">{saving ? "Saving…" : "SAVE SCENARIO"}</button>
            <button type="button" onClick={() => navigate("/dashboard")} data-testid="freedom-back-to-goal" className="min-h-11 flex-1 rounded-xl border border-[#E5E0D9] text-sm font-semibold text-[#164C3B] hover:bg-[#F5F2EC]">BACK TO MY GOAL</button>
          </div>
        </form>
      )}

      <p className="mt-3 flex items-start gap-1.5 text-xs leading-relaxed text-[#8A7C6B]">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        An educational, assumption-based scenario. You choose the withdrawal rate, current assets, contribution, and any
        growth assumption. The Paycheck Edit does not recommend investments, securities, or asset allocations, and guarantees
        no outcome.
      </p>
    </div>
  );
}
